function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useCallback } from 'react';
import { useValueChange } from '@leverege/ui-hooks';
import { Button, Flex, NumericInput, Pane, PropertyGrid, Selector, TabPane, Text } from '@leverege/ui-elements';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import { Plugins } from '@leverege/plugin';
import { ns } from '@leverege/i18n';
import { XYChartModel } from "../models/index.js";
import Series from "../Series.js";
import Coordinate from "../Coordinate.js";
import RolloverEditor from "./RolloverEditor.js";
import Thresholdizers from "../attribute/Thresholdizers.js";
import ThresholdsEditor from "./ThresholdsEditor.js";
import ThresholdModel from "../models/ThresholdModel.js";
const {
  t
} = ns('ui-attributes-graphs.editors.XYChartModelEditor');
const ORIENTATION_OPTIONS = [{
  get name() {
    return t('horizontal');
  },
  value: 'horizontal'
}, {
  get name() {
    return t('vertical');
  },
  value: 'vertical'
}];
const X_AXIS_POSITION_OPTIONS = [{
  get name() {
    return t('top');
  },
  value: 'top'
}, {
  get name() {
    return t('bottom');
  },
  value: 'bottom'
}];
const Y_AXIS_POSITION_OPTIONS = [{
  get name() {
    return t('left');
  },
  value: 'left'
}, {
  get name() {
    return t('right');
  },
  value: 'right'
}];
export default function XYChartModelEditor(props) {
  const {
    value,
    objectType,
    onChange,
    eventData,
    context
  } = props;
  const onModelChange = useValueChange(XYChartModel, props);
  const seriesType = XYChartModel.getSeriesType(value);
  const orienation = XYChartModel.getOrientation(value) || 'vertical';
  const isVertical = orienation === 'vertical';
  const xSelectorAndEditor = ModelTypeSelector.createSelectorAndEditor({
    values: Coordinate.getCoordinatesFor(objectType),
    value: isVertical ? XYChartModel.getXCoordinate(value) : XYChartModel.getYCoordinate(value),
    eventData: isVertical ? 'setXCoordinate' : 'setYCoordinate',
    onChange: onModelChange,
    allowNone: false,
    typeKey: 'name',
    isPropertyGrid: true,
    label: t('xCoordinateLabel')
  });
  const ySelectorAndEditor = ModelTypeSelector.createSelectorAndEditor({
    values: Coordinate.getCoordinatesFor(objectType),
    value: isVertical ? XYChartModel.getYCoordinate(value) : XYChartModel.getXCoordinate(value),
    eventData: isVertical ? 'setYCoordinate' : 'setXCoordinate',
    onChange: onModelChange,
    allowNone: false,
    typeKey: 'name',
    isPropertyGrid: true,
    label: t('yCoordinateLabel')
  });
  const onAddThreshold = useCallback(() => {
    const nModel = XYChartModel.addThreshold(value, ThresholdModel.create());
    onChange({
      data: eventData,
      value: nModel
    });
  }, [value]);
  return /*#__PURE__*/React.createElement(TabPane, {
    style: {
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "chart",
    tabName: t('chart')
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    columns: 2,
    variant: "xyChartEditor|editor",
    inline: true
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('xyChart')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    fillCell: true
  }, xSelectorAndEditor[0]), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    fillCell: true
  }, ySelectorAndEditor[0]), /*#__PURE__*/React.createElement(PropertyGrid.Divider, null), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('height')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getHeight(value),
    eventData: "setHeight",
    onChange: onModelChange,
    hint: t('heightHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('width')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getWidth(value),
    eventData: "setWidth",
    onChange: onModelChange,
    hint: t('widthHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('topMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getTopMargin(value),
    eventData: "setTopMargin",
    onChange: onModelChange,
    hint: t('topMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('rightMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getRightMargin(value),
    eventData: "setRightMargin",
    onChange: onModelChange,
    hint: t('rightMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('bottomMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getBottomMargin(value),
    eventData: "setBottomMargin",
    onChange: onModelChange,
    hint: t('bottomMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('leftMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getLeftMargin(value),
    eventData: "setLeftMargin",
    onChange: onModelChange,
    hint: t('leftMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('xLabelOffset')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getXLabelOffset(value),
    eventData: "setXLabelOffset",
    onChange: onModelChange,
    hint: t('xLabelOffsetHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('yLabelOffset')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: XYChartModel.getYLabelOffset(value),
    eventData: "setYLabelOffset",
    onChange: onModelChange,
    hint: t('yLabelOffsetHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('xAxisPosition')
  }, /*#__PURE__*/React.createElement(Selector, {
    value: XYChartModel.getXAxisPosition(value),
    eventData: "setXAxisPosition",
    onChange: onModelChange,
    values: X_AXIS_POSITION_OPTIONS
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('yAxisPosition')
  }, /*#__PURE__*/React.createElement(Selector, {
    value: XYChartModel.getYAxisPosition(value),
    eventData: "setYAxisPosition",
    onChange: onModelChange,
    values: Y_AXIS_POSITION_OPTIONS
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('orientation')
  }, /*#__PURE__*/React.createElement(Selector, {
    value: orienation,
    eventData: "setOrientation",
    onChange: onModelChange,
    values: ORIENTATION_OPTIONS
  })), /*#__PURE__*/React.createElement(PropertyGrid.Divider, null), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: /*#__PURE__*/React.createElement(Flex, {
      variant: "rowM",
      align: "center"
    }, /*#__PURE__*/React.createElement(Text, {
      variant: "subtitle"
    }, t('thresholds')), /*#__PURE__*/React.createElement(Button, {
      onClick: onAddThreshold,
      variant: "secondary",
      icon: t('addThresholdIcon', 'fa fa-fw fa-plus')
    }, t('addThreshold'))),
    fill: true
  }, /*#__PURE__*/React.createElement(ThresholdsEditor, _extends({}, props, {
    context: context,
    eventData: eventData,
    objectType: objectType,
    onChange: onChange,
    model: XYChartModel.thresholds(),
    value: value
  })))), /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('appearance')
  }), ModelTypeSelector.createSelectorAndEditor({
    values: Plugins.get('GraphBackgroundModel'),
    value: XYChartModel.getBackground(value),
    eventData: 'setBackground',
    onChange: onModelChange,
    allowNone: false,
    modelKey: 'model',
    isPropertyGrid: true,
    label: t('background'),
    propertyGridOpts: {
      inline: true
    }
  }), ModelTypeSelector.createSelectorAndEditor({
    values: Series.getSeriesFor(objectType),
    value: seriesType,
    eventData: 'setSeriesType',
    onChange: onModelChange,
    allowNone: false,
    modelKey: 'model',
    isPropertyGrid: true,
    label: t('series'),
    editorProps: {
      objectType
    }
  })))), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "xCoord",
    tabName: t('xCoordinate')
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "xAxisEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('xAxis')
  }), xSelectorAndEditor[1]))), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "yCoord",
    tabName: t('yCoordinate')
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "yAxisEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('yAxis')
  }), ySelectorAndEditor[1]))), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "rollover",
    tabName: t('rollover')
  }, /*#__PURE__*/React.createElement(RolloverEditor, {
    value: value,
    onChange: onModelChange,
    eventData: "setRollover",
    objectType: objectType
  })));
}